import { Dropdown } from "antd";
import cx from "clsx";
import { Avatar, Button, Divider, Icon, Text, Title } from "components/common";
import { Separator } from "components/custom";
import { redirectExternal } from "configs/utils";
import { ROLES_LISTING_PATH, TEAMS_LISTING_PATH, TENANT_SETTINGS_PATH } from "constants/app-paths";
import { getCurrentApp } from "core/appRegistry";
import { useLogoutMutation } from "features/auth/api";
import { getAppLanguage } from "helpers/getAppLanguage";
import { getLocalizedPath } from "helpers/getLocalizedPath";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "store/appSlice";
import parentApi from "store/parentApi";
import { joinBytItems, localisedValue, useNavigate } from "utils";
import getPrivilege from "utils/privileges/privileges";
import theme from "utils/themeVars";
import styles from "./userMenu.module.less";

const UserMenu = () => {
  const { t } = useTranslation()
  const user = useSelector((state) => state.app.user?.info?.user)
  const userPrivileges = useSelector((state) => state.app.privileges)
  const currentAppKey =
    useSelector((state) => state.app.currentApp) || getCurrentApp()?.key
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [logoutUser, { isLoading }] = useLogoutMutation()

  const handleLogout = () => {
    logoutUser({})
      .unwrap()
      .then(() => {
        dispatch(logout())
        dispatch(parentApi.util.resetApiState())
        redirectExternal(getLocalizedPath("/admin/login"))
      })
  }

  const items = [
    {
      label: <Text>{t("Users")}</Text>,
      key: "users",
      icon: <Icon icon="FiUsers" />,
      disabled: !getPrivilege("user_index", userPrivileges),
      onClick: () => {
        navigate("/staff")
      },
    },
    {
      label: <Text>{t("Teams")}</Text>,
      key: "teams",
      icon: <Icon icon="SvgMenuClients" />,
      disabled: !getPrivilege("polaris_team_index", userPrivileges),
      onClick: () => {
        navigate(TEAMS_LISTING_PATH)
      },
    },
    {
      label: <Text>{t("Roles")}</Text>,
      key: "roles",
      icon: <Icon icon="BsPersonFillGear" />,
      disabled: !getPrivilege("role_index", userPrivileges),
      onClick: () => {
        navigate(ROLES_LISTING_PATH)
      },
    },
    {
      label: <Text>{t("Tenant Settings")}</Text>,
      key: "tenant_settings",
      icon: <Icon icon="AiOutlineSetting" />,
      onClick: () => {
        navigate(TENANT_SETTINGS_PATH)
      },
      disabled: !getPrivilege("tenant_settings_update", userPrivileges),
    },
    {
      label: <Text color={theme["error-color"]}>{t("Logout")}</Text>,
      key: "logout",
      icon: <Icon color={theme["error-color"]} icon="FiLogOut" />,
      onClick: () => {
        handleLogout()
      },
    },
  ]
  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Dropdown
          menu={{ items }}
          dropdownRender={(menu) => (
            <div className={styles.userMenuOuter}>
              <div className={styles.userMenuHeader}>
                <div className={styles.userAvatar}>
                  <Avatar
                    size={45}
                    src={user?.profile_image}
                    key={user?.full_name}
                    colorKey={user?.full_name?.[0]?.toLowerCase()}
                  >
                    {user?.full_name?.[0]?.toUpperCase()}
                  </Avatar>
                </div>
                <div className={styles.userInfo}>
                  <Title level={5}>
                    {localisedValue({ object: user, key: "full_name" })}
                  </Title>
                  <Text>{user?.email}</Text>
                  <div>
                    <Text type="secondary">
                      {joinBytItems(
                        [
                          user?.designation
                            ? localisedValue({
                                object: user?.designation,
                                key: "name",
                              })
                            : "",
                          user?.roles?.find(
                            (role) => role?.app?.slug == currentAppKey
                          )
                            ? localisedValue({
                                object: user?.roles?.find(
                                  (role) => role?.app?.slug == currentAppKey
                                ),
                                key: "name",
                              })
                            : "",
                        ],
                        <Separator />
                      )}
                    </Text>
                  </div>
                </div>
              </div>
              <Divider style={{ marginBlock: "12px" }} />
              <div className={cx("fs16 ", styles.listItems)}>{menu}</div>
              {user?.apps?.length > 1 && (
                <Divider style={{ marginBlock: "12px" }} />
              )}
              {currentAppKey === "rms"
                ? user?.apps?.find((app) => app?.slug === "fms") && (
                    <Button
                      type="primary"
                      icon="HiSwitchHorizontal"
                      iconProps={{ color: "#FFFFFF", size: "18px" }}
                      block
                      onClick={() => {
                        window.location.href = `${window.location.origin}/${
                          getAppLanguage()?.locale
                        }/fms`
                      }}
                    >
                      {t("Facility Management")}
                    </Button>
                  )
                : currentAppKey === "fms"
                ? user?.apps?.find((app) => app?.slug === "rms") && (
                    <Button
                      type="primary"
                      icon="HiSwitchHorizontal"
                      iconProps={{ color: "#FFFFFF", size: "18px" }}
                      block
                      onClick={() => {
                        window.location.href = `${window.location.origin}/en/rms`
                      }}
                    >
                      {t("Booking Management")}
                    </Button>
                  )
                : null}
            </div>
          )}
          className="d-grid"
        >
          <div>
            <Button
              type="light"
              color={theme["primary-color"]}
              shape="circle"
              style={{ padding: 0, width: 36, height: 36 }}
            >
              <Avatar
                size={32}
                src={user?.profile_image}
                key={user?.full_name}
                colorKey={user?.full_name?.[0]?.toLowerCase()}
              >
                {user?.full_name?.[0]?.toUpperCase()}
              </Avatar>
            </Button>
          </div>
        </Dropdown>
      </div>
    </>
  )
}

export default UserMenu;
